/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, bool, string } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import classNames from 'clsx';
import jump from 'jump.js';

import { getStaticPage as getStaticPageAction } from 'actions/staticPage';

import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import NotFound from 'components/NotFound/NotFound';
import Select from 'components/Form/Input/Select/Select';

import styles from './static-page.scss';

/**
 * This is the StaticPage component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class StaticPage extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    match: shape({
      params: shape({
        slug: string.isRequired,
        section: string,
      }).isRequired,
    }).isRequired,
    auth: shape({
      token: string,
    }).isRequired,
    staticPage: shape({
      loading: bool.isRequired,
      notFound: bool.isRequired,
    }).isRequired,
    getStaticPage: func.isRequired,
  };

  /**
   * Create StaticPage.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.sections = [];
  }

  componentDidMount() {
    const {
      auth: { token },
      match: { params },
      getStaticPage,
    } = this.props;

    getStaticPage(token, params.slug);
  }

  componentDidUpdate(prevProps) {
    const nextProps = this.props;
    const {
      match: { params },
    } = nextProps;
    const { staticPage } = prevProps;

    if (staticPage.loading && !nextProps.staticPage.loading) {
      process.nextTick(() => this.goToSection(params.section));
    }
  }

  onChange = event => {
    const slug = event.target.value;

    this.goToSection(slug);
  };

  goToSection = slug => {
    if (this.sections[slug]) {
      jump(this.sections[slug], {
        offset: -100,
        // duration: distance => Math.abs(distance * 1.5),
      });
    }
  };

  renderMenu() {
    const {
      staticPage: { title: pageTitle, sections },
      match: { params },
    } = this.props;
    const mobileMenu = [];

    sections.map(({ slug, title }) => (mobileMenu[slug] = title));

    return [
      <nav className={styles.menu} key="desktop-menu">
        <ul>
          {sections.map(({ slug, title }) => (
            <li key={`menu${slug}`}>
              <button
                className={styles.menuLink}
                onClick={() => this.goToSection(slug)}
              >
                {title}
              </button>
            </li>
          ))}
        </ul>
      </nav>,
      <nav className={styles.mobileMenu} key="mobile-menu">
        <Select
          placeholder={pageTitle}
          name="mobile-menu"
          value={params.section}
          options={mobileMenu}
          onChange={this.onChange}
          required
        />
      </nav>,
    ];
  }

  /**
   * Render StaticPage.
   *
   * @return {Object}
   */
  render() {
    const {
      staticPage: { notFound, loading, title, sections },
    } = this.props;

    const hasSections = sections && sections.length > 1;

    if (loading) {
      return (
        <section className={styles.wrapper}>
          <div className={styles.spinner}>
            <SpinnerIcon medium />
          </div>
        </section>
      );
    }

    if (notFound) {
      return <NotFound />;
    }

    return (
      <section className={styles.wrapper}>
        <div className={styles.row}>
          <header className={styles.header}>
            <h1>{title}</h1>
          </header>
        </div>

        <div className={styles.row}>
          {hasSections && this.renderMenu()}

          <div
            className={classNames(styles.content, {
              [styles.compact]: hasSections,
            })}
          >
            {sections.map(({ slug, title, content }) => (
              <article
                key={slug}
                className={styles.section}
                ref={ref => {
                  this.sections[slug] = ref;
                }}
              >
                <h2 className={styles.sectionTitle}>{title}</h2>

                <div
                  className={styles.sectionContent}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  }
}

export default withRouter(
  connect(({ auth, staticPage }) => ({ auth, staticPage }), {
    getStaticPage: getStaticPageAction,
  })(StaticPage)
);
